import React, { FC, memo, useState } from "react";
import { ItemMetaData } from "../../../../interfaces/Metadata";
import { ItemRetailAdjustment } from "../../../../interfaces/ItemRetailAdjustment";
import { colors, LabeledInput, Text } from "@commonsku/styles";
import { LabeledSelect } from "../../../helpers/Select";
import { TNumber } from "../../../../interfaces/common";

interface QuestionProps {
  m: ItemMetaData;
  adjustments: ItemRetailAdjustment[];
  value: string;
  onUpdateProductMetadata: (
    metadata_id: string,
    label: string,
    value: string,
    character_limit: number,
    upcharge: string | null,
    parent_id: string,
    type: string,
    mandatory: TNumber,
    display_order: TNumber,
    options: {
      key: string;
      value: string;
    } | null,
    shop_id: string
  ) => void;
  shop_id: string;
}

const Question: FC<QuestionProps> = memo(
  ({ m, adjustments, value, onUpdateProductMetadata, shop_id }) => {
    const [isTouched, setIsTouched] = useState<boolean>(false);
    const isRequired = "" + m.mandatory === "1";
    const options =
      m.type === "SELECT"
        ? JSON.parse(m.value || "[]")
          .filter((v: string) => v.trim() !== "")
          .map((v: string) => {
            const adjustment = adjustments.find(a => a.option === v);
            let optionValue = v;
            if (adjustment) {
              const amount = parseFloat(adjustment.adjustment);
              if (!isNaN(amount) && amount !== 0) {
                optionValue = `${v} (+ $${adjustment.adjustment})`;
              }
            }
            return {
              key: v,
              value: optionValue,
            };
          }) : [];

    const optionsForRender =
      m.type === "SELECT"
        ? JSON.parse(m.value || "[]")
          .filter((v: string) => v.trim() !== "")
          .map((v: string) => {
            const adjustment = adjustments.find(a => a.option === v);
            return {
              key: v,
              value: (
                <>
                  {v}
                  {adjustment &&
                    !isNaN(parseFloat(adjustment.adjustment)) &&
                    parseFloat(adjustment.adjustment) !== 0 && (
                      <strong>(+ ${adjustment.adjustment})</strong>
                    )}
                </>
              ),
            };
          }) : [];

    let textLabel = m.label;
    let upcharge = null;
    if (m.type === "TEXT") {
      const adjustment = adjustments.find(a => a.option == null);
      if (adjustment) {
        const amount = parseFloat(adjustment.adjustment);
        if (!isNaN(amount) && amount !== 0) {
          textLabel = `${m.label} (+ $${adjustment.adjustment})`;
          upcharge = adjustment.adjustment;
        }
      }
    }

    const characterLimit = Number(m.properties?.characterLimit);
    const currentCharacterCount = value.length;
    const isOverLimit = m.type === "TEXT" && !isNaN(characterLimit) && characterLimit > 0 && currentCharacterCount > characterLimit;
    const hasError = (isRequired && !value.trim() && isTouched) || isOverLimit;

    return [
      m.type === "SELECT" ? (
        <LabeledSelect
          key={m.metadata_id}
          name={"metadata-item-" + m.metadata_id}
          id={"metadata-item-" + m.metadata_id}
          required={isRequired}
          label={`${m.label}${isRequired ? "" : " (optional)"}`}
          placeholder={m.label}
          value={value}
          onChange={(e: any /** Need better type inference */) =>
            onUpdateProductMetadata(
              m.metadata_id,
              m.label,
              e.value,
              characterLimit,
              upcharge,
              m.parent_id,
              m.type,
              m.mandatory,
              m.display_order,
              options,
              shop_id
            )
          }
          options={optionsForRender}
          error={hasError}
          parentStyle={{ marginBottom: 14 }}
          data-test-id={`product-question-${m.metadata_id}`}
          onBlur={() => setIsTouched(true)}
        />
      ) : (
        <div style={{ position: "relative", ...(hasError ? { marginBottom: 0 } : {})}}>
          <LabeledInput
            key={m.metadata_id}
            id={"metadata-item-" + m.metadata_id}
            required={isRequired}
            label={`${textLabel}${isRequired ? "" : " (optional)"}`}
            value={value}
            onChange={(e) =>
              onUpdateProductMetadata(
                m.metadata_id,
                m.label,
                e.target.value,
                characterLimit,
                upcharge,
                m.parent_id,
                m.type,
                m.mandatory,
                m.display_order,
                null,
                shop_id
              )
            }
            placeholder={m.label}
            error={hasError}
            style={{
              ...(hasError ? { marginBottom: 0 } : {}),
              ...(!isNaN(characterLimit) && characterLimit > 0 ? { paddingRight : 70 } : {}),
            }}
            data-test-id={`product-question-${m.metadata_id}`}
            onBlur={() => setIsTouched(true)}
          />
          {!isNaN(characterLimit) && characterLimit > 0 && (
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                ...(isOverLimit ? { color : "red" } : {}),
                transform: !hasError ? "translateY(-30%)" : undefined,
              }}
            >
              {currentCharacterCount} / {characterLimit}
            </span>
          )}
        </div>
      ),
      hasError && (
        <Text
          key={"error-" + m.metadata_id}
          as="p"
          style={{ marginBottom: 16, color: colors.errors.main }}
        >
          {isOverLimit
            ? `Please limit your input to ${characterLimit} characters`
            : "This field is required"
          }
        </Text>
      ),
    ];
  }
);

export default Question;
